// AWENI motion helpers — thin wrapper over Motion's vanilla `animate()`.
//
// Why this file exists:
// - Single source of truth for animation defaults across all AWENI modules
// - Globally honors `prefers-reduced-motion` (a11y rule from CLAUDE.md)
// - Respects the ≤500ms duration ceiling from ux-polish.md
// - Reads timing from CSS custom properties so design system v1 owns the values
//
// Bundle cost: importing { animate, inView } from 'motion' = ~2.3 KB gzipped
// (the vanilla mini build, NOT the React 46 KB build).
//
// Usage:
//   import { aweniAnimate, aweniInView, prefersReducedMotion } from '@/utils/motion';
//   aweniAnimate(el, { opacity: [0, 1], y: [12, 0] }, 'enter');

import { animate, inView, type AnimationPlaybackControls } from 'motion';

// AWENI duration tokens (ms) — keep aligned with ux-polish.md "≤500ms" ceiling.
export const DURATION = {
  instant: 80,
  fast: 160,
  base: 240,
  slow: 360,
  cinematic: 480, // hard ceiling — never exceed without reason
} as const;

// AWENI easing tokens — cubic-bezier curves only. Spring is opt-in via overrides.
type Cubic = [number, number, number, number];
export const EASE = {
  standard: [0.4, 0, 0.2, 1] as Cubic,
  decelerate: [0, 0, 0.2, 1] as Cubic,
  accelerate: [0.4, 0, 1, 1] as Cubic,
} as const;

// Pass this as overrides to aweniAnimate when you want spring physics:
//   aweniAnimate(el, { y: [12, 0] }, 'enter', SPRING_GENTLE);
export const SPRING_GENTLE = { type: 'spring' as const, stiffness: 300, damping: 30 };

type Intent = 'enter' | 'exit' | 'attention' | 'micro';

const INTENT_DEFAULTS: Record<Intent, { duration: number; ease: Cubic }> = {
  enter: { duration: DURATION.slow / 1000, ease: EASE.decelerate },
  exit: { duration: DURATION.fast / 1000, ease: EASE.accelerate },
  attention: { duration: DURATION.base / 1000, ease: EASE.standard },
  micro: { duration: DURATION.fast / 1000, ease: EASE.standard },
};

let cachedReducedMotion: boolean | null = null;

export function prefersReducedMotion(): boolean {
  if (cachedReducedMotion !== null) return cachedReducedMotion;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  cachedReducedMotion = mq.matches;
  mq.addEventListener?.('change', (e) => {
    cachedReducedMotion = e.matches;
  });
  return cachedReducedMotion;
}

/**
 * Animate an element with AWENI's intent-driven defaults.
 * If user prefers reduced motion, applies the end state instantly.
 */
export function aweniAnimate(
  element: Element | Element[] | NodeListOf<Element>,
  keyframes: Parameters<typeof animate>[1],
  intent: Intent = 'enter',
  overrides: Parameters<typeof animate>[2] = {},
): AnimationPlaybackControls | null {
  const defaults = INTENT_DEFAULTS[intent];
  if (prefersReducedMotion()) {
    // Skip the tween, snap to final values
    const els = element instanceof Element ? [element] : Array.from(element as Iterable<Element>);
    els.forEach((el) => {
      Object.entries(keyframes as Record<string, unknown>).forEach(([prop, val]) => {
        const final = Array.isArray(val) ? (val as unknown[])[(val as unknown[]).length - 1] : val;
        if (final !== undefined && el instanceof HTMLElement) {
          (el.style as unknown as Record<string, string>)[prop] = String(final);
        }
      });
    });
    return null;
  }
  return animate(element as never, keyframes, { ...defaults, ...overrides });
}

/**
 * Stagger-fade a list of elements with the standard 60ms cascade
 * (ux-polish.md says 50–70ms — we pick 60).
 */
export function aweniStaggerIn(
  elements: Element[] | NodeListOf<Element>,
  delayPerItem = 60,
): void {
  const els = Array.from(elements);
  if (prefersReducedMotion()) {
    els.forEach((el) => {
      if (el instanceof HTMLElement) el.style.opacity = '1';
    });
    return;
  }
  els.forEach((el, i) => {
    aweniAnimate(el, { opacity: [0, 1], y: [8, 0] }, 'enter', {
      delay: (i * delayPerItem) / 1000,
    });
  });
}

/**
 * Reveal-on-scroll using Motion's `inView` (IntersectionObserver under the hood).
 */
export function aweniRevealOnScroll(
  selector: string,
  intent: Intent = 'enter',
): void {
  if (prefersReducedMotion()) return;
  inView(selector, (element) => {
    aweniAnimate(element, { opacity: [0, 1], y: [16, 0] }, intent);
  });
}

/**
 * Quick attention pulse for "correct" feedback (ux-polish.md: green pulse).
 * Used by Memoriz, Games, BalanceTab confirmations.
 */
export function aweniPulseSuccess(element: Element): AnimationPlaybackControls | null {
  return aweniAnimate(
    element,
    {
      scale: [1, 1.04, 1],
      filter: ['drop-shadow(0 0 0 rgba(0,200,83,0))', 'drop-shadow(0 0 12px rgba(0,200,83,0.55))', 'drop-shadow(0 0 0 rgba(0,200,83,0))'],
    },
    'attention',
  );
}

/**
 * Horizontal shake for "wrong" feedback (ux-polish.md: 4 oscillations, ≤8px).
 */
export function aweniShakeError(element: Element): AnimationPlaybackControls | null {
  return aweniAnimate(
    element,
    { x: [0, -8, 8, -6, 6, -4, 4, 0] },
    'attention',
    { duration: 0.36 },
  );
}
